'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Search } from 'lucide-react'
import { SearchInput, FilterTags, MobileSearchSheet } from '@/components/search'
import { useAuth } from '@/components/auth'
import { useSearchStore } from '@/lib/store'
import { useTypewriter } from '@/lib/useTypewriter'
import { REQUIRE_AUTH_FOR_SEARCH } from '@/lib/feature-flags'
import { cn } from '@/lib/utils'

// Rotating examples for the mobile trigger placeholder (shared with the gate's
// AI feature widget via export).
export const EXAMPLE_QUERIES = [
  '2 rooms, under 25k, Praha 3',
  '1+ room, under 10k total',
  '20 min from Anděl metro, furnished',
  '2 rooms, balcony',
  '3+ rooms, parking',
  'in Praha 7',
  '15 min from Strossmayerovo náměstí',
  'near Florenc, furnished',
  'in Praha 12, pet friendly',
  '2+1, balcony, under 20k',
  'dishwasher, washing machine',
  'with desk',
  'bathtub, under 25k',
  'bright, balcony',
]

// HomeSearchEntry (§25.12) — the real natural-language search box + quick filter
// tags, extracted from the signed-in SearchHome so the signed-OUT welcome gate
// can offer the same guest search (meeting decision: don't force login to try
// the service). Desktop shows the full SearchInput + FilterTags; mobile shows a
// tappable trigger that opens MobileSearchSheet. Submitting routes to /search,
// which runs the query anonymously (rate-limited, no auth required) — unless
// REQUIRE_AUTH_FOR_SEARCH is on, in which case signed-out submits open the
// sign-in modal instead of navigating.
export function HomeSearchEntry({
  showFilterTags = true,
  showQueryChips = false,
  className,
}: {
  showFilterTags?: boolean
  /** Gate-only quick query chips (see SearchInput.showQueryChips). */
  showQueryChips?: boolean
  className?: string
}) {
  const router = useRouter()
  const { user, signIn } = useAuth()
  const {
    query,
    filters,
    selectedCity,
    selectedDistricts,
    setSelectedCity,
    setSelectedDistricts,
    setHasSearched,
  } = useSearchStore()

  const [isMobile, setIsMobile] = React.useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false)

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const animatedPlaceholder = useTypewriter(EXAMPLE_QUERIES, {
    typingSpeed: 80,
    deletingSpeed: 40,
    pauseDuration: 2500,
    paused: mobileSearchOpen,
  })

  const handleSearch = () => {
    if (REQUIRE_AUTH_FOR_SEARCH && !user) {
      signIn()
      return
    }
    setHasSearched(true)
    const params = new URLSearchParams()
    if (query) params.set('q', query)
    if (filters.transaction_type && filters.transaction_type !== 'rent') {
      params.set('transaction_type', filters.transaction_type)
    }
    if (selectedCity && selectedCity !== 'Praha') params.set('city', selectedCity)
    if (selectedDistricts.length > 0) params.set('districts', selectedDistricts.join(','))
    router.push(`/search${params.toString() ? `?${params.toString()}` : ''}`)
  }

  return (
    <div className={className}>
      {/* mb-6 only spaces the FilterTags row below — no dead margin when the
          caller hides tags (gate hero). max-w-3xl (widened 2026-07-08 for
          longer NL queries) must match the gate's search wrapper so the input
          fills it and stays left-aligned on the gate instead of centering. */}
      <div className={cn('mx-auto max-w-3xl', showFilterTags && 'mb-6')}>
        {isMobile ? (
          <button
            onClick={() => setMobileSearchOpen(true)}
            className={cn(
              'w-full h-16 px-5 rounded-2xl',
              // Solid card + 2px border like the desktop input — the old
              // bg-card/50 was near-invisible over the hero illustration.
              'bg-card border-2 border-border shadow-sm',
              'text-muted-foreground text-base text-left',
              'hover:border-primary/40 transition-all duration-200',
              'flex items-center gap-3'
            )}
          >
            <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <span className="truncate">{animatedPlaceholder || ' '}</span>
            <span className="inline-block w-0.5 h-5 bg-muted-foreground/40 animate-pulse" />
          </button>
        ) : (
          <SearchInput
            onSearch={handleSearch}
            selectedCity={selectedCity}
            onCityChange={setSelectedCity}
            selectedDistricts={selectedDistricts}
            onDistrictsChange={setSelectedDistricts}
            showQueryChips={showQueryChips}
          />
        )}
      </div>

      {showFilterTags && !isMobile && (
        <div className="mx-auto max-w-4xl">
          <FilterTags onSearch={handleSearch} />
        </div>
      )}

      <MobileSearchSheet
        open={mobileSearchOpen}
        onOpenChange={setMobileSearchOpen}
        onSearch={handleSearch}
        showQueryChips={showQueryChips}
      />
    </div>
  )
}
